import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    LayoutDashboard,
    Users,
    BookOpen,
    Calendar,
    CheckSquare,
    Layers,
    GraduationCap,
    AlertCircle,
    X
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function Sidebar({ isOpen, closeSidebar }) {
    const { user } = useAuth();
    const role = user?.role || 'STUDENT';

    const links = [
        {
            label: 'Dashboard',
            path: '/dashboard',
            icon: LayoutDashboard,
            roles: ['SUPER_ADMIN', 'HOD', 'FACULTY', 'STUDENT', 'PRINCIPAL']
        },
        {
            label: 'Departments',
            path: '/dashboard/departments-setup',
            icon: Layers,
            roles: ['PRINCIPAL']
        },
        {
            label: 'Staff Directory',
            path: '/dashboard/staff',
            icon: Users,
            roles: ['PRINCIPAL']
        },
        {
            label: 'Student Directory',
            path: '/dashboard/students',
            icon: GraduationCap,
            roles: ['PRINCIPAL', 'HOD']
        },
        {
            label: 'Academic Setup',
            path: '/dashboard/academic',
            icon: Layers,
            roles: ['HOD']
        },
        {
            label: 'Subjects',
            path: '/dashboard/subjects',
            icon: BookOpen,
            roles: ['HOD']
        },
        {
            label: 'Sections',
            path: '/dashboard/sections',
            icon: Users,
            roles: ['HOD']
        },
        {
            label: 'Faculty Mapping',
            path: '/dashboard/mappings',
            icon: Users,
            roles: ['HOD']
        },
        {
            label: 'Timetable',
            path: '/dashboard/timetable',
            icon: Calendar,
            roles: ['HOD', 'FACULTY']
        },
        {
            label: 'My Timetable',
            path: '/dashboard/my-timetable',
            icon: Calendar,
            roles: ['STUDENT']
        },
        {
            label: 'My Classes',
            path: '/dashboard/classes',
            icon: BookOpen,
            roles: ['FACULTY']
        },
        {
            label: 'Attendance',
            path: '/dashboard/attendance',
            icon: CheckSquare,
            roles: ['FACULTY']
        },
        {
            label: 'My Attendance',
            path: '/dashboard/my-attendance',
            icon: CheckSquare,
            roles: ['STUDENT']
        },
        {
            label: 'My Profile',
            path: '/dashboard/my-attendance',
            icon: Users,
            roles: ['STUDENT']
        },
        {
            label: 'Leave Management',
            path: '/dashboard/leaves',
            icon: AlertCircle,
            roles: ['FACULTY', 'HOD', 'PRINCIPAL']
        },
    ];

    const filteredLinks = links.filter(link => link.roles.includes(role));

    return (
        <div className={cn(
            "fixed inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col z-40 transition-transform duration-300 transform lg:translate-x-0",
            isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"
        )}>
            <div className="p-6 flex items-center justify-between border-b border-border/50">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                        <GraduationCap size={24} />
                    </div>
                    <div>
                        <h2 className="font-heading font-bold text-lg text-foreground leading-tight">Academic<br />Portal</h2>
                    </div>
                </div>
                <button
                    onClick={closeSidebar}
                    className="p-2 text-muted-foreground hover:bg-secondary rounded-lg lg:hidden"
                >
                    <X size={20} />
                </button>
            </div>

            <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">
                    Menu
                </div>
                {filteredLinks.map((link) => (
                    <NavLink
                        key={link.label + link.path}
                        to={link.path}
                        onClick={() => {
                            if (window.innerWidth < 1024) closeSidebar();
                        }}
                        className={({ isActive }) => cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 group",
                            isActive
                                ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                                : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
                        )}
                    >
                        {({ isActive }) => (
                            <>
                                <link.icon size={20} className={isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-secondary-foreground"} />
                                <span>{link.label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="p-4 border-t border-border/50 bg-card/50">
                <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-secondary/50">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm overflow-hidden flex-shrink-0">
                        {user?.avatar ? (
                            <img src={user.avatar} alt="User" className="h-full w-full object-cover" />
                        ) : (
                            <span>{user?.name?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || 'U'}</span>
                        )}
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-medium truncate text-foreground">{user?.name || user?.username || 'User'}</p>
                        <p className="text-[10px] font-bold text-primary/80 uppercase tracking-tighter">{role}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
